import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../api/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function FacturarPedido() {
  const { pedidoId } = useParams();
  const navigate = useNavigate();

  const [pedido, setPedido] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [total, setTotal] = useState(0);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [mostrarPagoEfectivo, setMostrarPagoEfectivo] = useState(false);
  const [montoPago, setMontoPago] = useState("");
  const [vueltos, setVueltos] = useState(0);
  const [modo, setModo] = useState(""); // "TODO" o "PARCIAL"
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [facturaPendiente, setFacturaPendiente] = useState(null);

  // 🧾 Nueva: para imprimir factura
  const [facturaGeneradaId, setFacturaGeneradaId] = useState(null);
  const [ticketFactura, setTicketFactura] = useState("");

  // === Cargar pedido ===
  useEffect(() => {
    obtenerPedido();
  }, [pedidoId]);

  const obtenerPedido = async () => {
    try {
      const res = await axios.get(`/pedidos/${pedidoId}`);
      const data = res.data;
      setPedido(data);

      const detallesProcesados = data.detalles.map((d) => ({
        id: d.id, // id del DetallePedido
        productoId: d.producto?.id,
        producto: d.producto?.nombre || "—",
        cantidad: d.cantidad,
        precio: d.precio || (d.cantidad > 0 ? d.subtotal / d.cantidad : 0),
        subtotal: d.subtotal,
        descripcion: d.descripcion || "",
        cobrado: d.cobrado || false,
      }));

      setDetalles(detallesProcesados);
      setSeleccionados(detallesProcesados.map(() => false));
      calcularTotal(detallesProcesados);
    } catch (error) {
      console.error("Error al obtener el pedido:", error);
      toast.error("❌ No se pudo cargar el pedido");
    }
  };

  const calcularTotal = (lista) => {
    const totalCalc = lista.reduce(
      (acc, d) => acc + (parseFloat(d.subtotal) || 0),
      0
    );
    setTotal(totalCalc);
  };

  const calcularTotalSeleccionados = () =>
    detalles
      .filter((_, idx) => seleccionados[idx])
      .reduce((acc, d) => acc + (parseFloat(d.subtotal) || 0), 0);

  const toggleSeleccion = (index) => {
    const nuevos = [...seleccionados];
    nuevos[index] = !nuevos[index];
    setSeleccionados(nuevos);
  };

  const seleccionarTodos = (valor) => {
    // Solo marca los que no estén cobrados
    setSeleccionados(detalles.map((d) => (d.cobrado ? false : valor)));
  };

  // === Confirmar creación de factura ===
  const confirmarFactura = async () => {
    try {
      const productosAFacturar =
        modo === "TODO"
          ? detalles.filter((d) => !d.cobrado)
          : detalles.filter(
              (_, idx) => seleccionados[idx] && !detalles[idx].cobrado
            );

      if (productosAFacturar.length === 0) {
        toast.warning("⚠️ No hay productos seleccionados para cobrar");
        return;
      }

      const totalFactura = productosAFacturar.reduce(
        (acc, d) => acc + (parseFloat(d.subtotal) || 0),
        0
      );

      const detallesFactura = productosAFacturar.map((d) => ({
        detallePedidoId: d.id,
        producto: { id: d.productoId },
        cantidad: d.cantidad,
        precioUnitario: d.precio,
        subtotal: d.subtotal,
      }));

      const body = {
        pedido: { id: pedido.id },
        metodoPago: metodoPago,
        total: totalFactura,
        // Solo se marca pagada si NO es efectivo
        pago: metodoPago !== "EFECTIVO",
        detalles: detallesFactura,
      };

      if (metodoPago === "EFECTIVO") {
        setFacturaPendiente(body);
        setMontoPago("");
        setVueltos(0);
        setMostrarPagoEfectivo(true);
        return;
      }

      // Otros métodos (TARJETA, TRANSFERENCIA)
      await crearFactura(body);
    } catch (error) {
      console.error("Error al facturar:", error.response || error);
      toast.error("❌ Error al generar la factura");
    }
  };

  // === Crear factura (llama al backend) ===
  // Devuelve true si se creó, false si falló
  const crearFactura = async (body) => {
    try {
      const res = await axios.post("/facturas", body);
      console.log("Factura creada:", res.data);

      // 🧾 Guardamos el id para impresión
      setFacturaGeneradaId(res.data.id);

      toast.success("✅ Factura generada correctamente");

      // Marcar como cobrados solo los detalles incluidos en la factura
      const nuevosDetalles = detalles.map((d) =>
        body.detalles.some((df) => df.detallePedidoId === d.id)
          ? { ...d, cobrado: true }
          : d
      );

      setDetalles(nuevosDetalles);
      setSeleccionados(nuevosDetalles.map(() => false));

      // Si todos están cobrados, marcar pedido como facturado
      const todosCobrados = nuevosDetalles.every((d) => d.cobrado);
      if (todosCobrados) {
        await axios.put(`/pedidos/${pedido.id}`, {
          ...pedido,
          facturado: true,
        });
        setPedido({ ...pedido, facturado: true });
      }

      setMostrarConfirmacion(true);
      return true;
    } catch (error) {
      console.error("Error creando factura:", error);

      let mensaje = "❌ No se pudo crear la factura";
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (typeof data === "string") {
          mensaje = data;
        } else if (data.message) {
          mensaje = data.message;
        }
      }

      toast.error(mensaje);
      return false;
    }
  };

  // === Confirmar pago en efectivo ===
  const confirmarPagoEfectivo = async () => {
    const monto = parseFloat(montoPago);
    const totalFactura = facturaPendiente.total;

    if (isNaN(monto) || monto < totalFactura) {
      toast.warning("💰 El monto pagado debe ser mayor o igual al total");
      return;
    }

    const body = { ...facturaPendiente, pago: true };
    const ok = await crearFactura(body);

    if (!ok) {
      // Hubo error, no mostramos vueltos
      return;
    }

    const vuelto = monto - totalFactura;
    setVueltos(vuelto);
    setMostrarPagoEfectivo(false);

    toast.success(`💵 Pago recibido. Vueltos: $${vuelto.toFixed(2)}`);
  };

  // === Obtener ticket de la factura ===
  const obtenerTicketFactura = async () => {
    if (!facturaGeneradaId) {
      toast.warning("⚠️ No hay factura para imprimir");
      return null;
    }

    try {
      const res = await axios.get(`/facturas/${facturaGeneradaId}/ticket`);
      setTicketFactura(res.data);
      return res.data;
    } catch (error) {
      console.error("Error obteniendo ticket de factura:", error);
      toast.error("❌ No se pudo generar el ticket de la factura");
      return null;
    }
  };

  // === Imprimir factura ===
  const imprimirFactura = async () => {
    try {
      const ticket = ticketFactura || (await obtenerTicketFactura());
      if (!ticket) return;

      await axios.post("/imprimir", {
        impresora: "POS-80C",
        contenido: ticket,
      });

      toast.success("🖨️ Factura enviada a impresión");
    } catch (error) {
      console.error("Error al imprimir factura:", error);
      toast.error("❌ Error al imprimir la factura");
    }
  };

  // === Lógica de activación de botones ===
  const haySeleccionados = seleccionados.some(
    (v, idx) => v && !detalles[idx].cobrado
  );

  const todosSeleccionados =
    detalles.length > 0 &&
    detalles.every((d, idx) => d.cobrado || seleccionados[idx]);

  if (!pedido) {
    return (
      <div className="mt-10 flex flex-col items-center justify-center text-slate-700 dark:text-slate-100">
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm">Cargando pedido...</p>
      </div>
    );
  }

  return (
    <div className="mt-4 px-3 md:px-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Card principal */}
      <div className="bg-white/95 dark:bg-slate-900/90 text-slate-900 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/70">
          <div className="flex items-center gap-2">
            <span className="text-emerald-500 dark:text-emerald-300">💰</span>
            <div>
              <h2 className="text-base md:text-lg font-semibold text-slate-800 dark:text-slate-100">
                Facturar Pedido #{pedido.id}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Selecciona los productos a cobrar y el método de pago.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-400 dark:border-slate-600 text-xs md:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            ⬅️ Volver
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Datos básicos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs sm:text-sm">
            <div>
              <span className="font-semibold text-slate-600 dark:text-slate-300">
                Mesa:{" "}
              </span>
              <span className="text-slate-800 dark:text-slate-100">
                {pedido.mesa?.numero || "—"}
              </span>
            </div>
            <div>
              <span className="font-semibold text-slate-600 dark:text-slate-300">
                Mesero:{" "}
              </span>
              <span className="text-slate-800 dark:text-slate-100">
                {pedido.mesero?.nombre || "—"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-600 dark:text-slate-300">
                Estado:
              </span>
              {pedido.facturado ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                  Facturado
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  Pendiente
                </span>
              )}
            </div>
          </div>

          {/* Tabla */}
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-100 flex items-center gap-2 mt-1">
            <span>🧾</span>
            <span>Detalles del pedido</span>
          </h3>

          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
            <table className="min-w-full text-xs sm:text-sm">
              <thead className="bg-slate-900 text-slate-100">
                <tr className="text-center align-middle">
                  <th className="px-3 py-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-emerald-500"
                      checked={todosSeleccionados}
                      onChange={(e) => seleccionarTodos(e.target.checked)}
                      disabled={pedido.facturado}
                    />
                  </th>
                  <th className="px-3 py-2 font-semibold">Producto</th>
                  <th className="px-3 py-2 font-semibold">Cant.</th>
                  <th className="px-3 py-2 font-semibold">Precio</th>
                  <th className="px-3 py-2 font-semibold">Descripción</th>
                  <th className="px-3 py-2 font-semibold">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {detalles.map((d, index) => (
                  <tr
                    key={index}
                    className={
                      d.cobrado
                        ? "bg-emerald-50 dark:bg-emerald-900/20 text-slate-400 dark:text-slate-400"
                        : "odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/70 dark:even:bg-slate-900/50 text-slate-800 dark:text-slate-100"
                    }
                  >
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-emerald-500"
                        checked={seleccionados[index]}
                        disabled={d.cobrado || pedido.facturado}
                        onChange={() => toggleSeleccion(index)}
                      />
                    </td>
                    <td className="px-3 py-2 text-left">{d.producto}</td>
                    <td className="px-3 py-2 text-center">{d.cantidad}</td>
                    <td className="px-3 py-2 text-center">
                      ${parseFloat(d.precio).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-left">
                      {d.descripcion || "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      ${parseFloat(d.subtotal).toFixed(2)}
                    </td>
                  </tr>
                ))}

                {detalles.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-4 text-center text-xs sm:text-sm text-slate-500 dark:text-slate-300"
                    >
                      No hay detalles para este pedido.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-2">
            <h5 className="text-base md:text-lg font-semibold text-emerald-500 dark:text-emerald-400">
              Total pedido: ${total.toFixed(2)}
            </h5>
            <h6 className="text-xs sm:text-sm font-semibold text-sky-700 dark:text-sky-300">
              Total seleccionado: ${calcularTotalSeleccionados().toFixed(2)}
            </h6>
          </div>

          {/* Método de pago + botones */}
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
              <span className="font-semibold text-slate-700 dark:text-slate-100">
                Método de pago:
              </span>
              <select
                value={metodoPago}
                onChange={(e) => setMetodoPago(e.target.value)}
                disabled={pedido.facturado}
                className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 px-3 py-1.5 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              >
                <option value="EFECTIVO">Efectivo</option>
                <option value="TARJETA">Tarjeta</option>
                <option value="TRANSFERENCIA">Transferencia</option>
              </select>
            </div>

            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                disabled={pedido.facturado || !haySeleccionados}
                onClick={() => {
                  setModo("PARCIAL");
                  confirmarFactura();
                }}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  pedido.facturado || !haySeleccionados
                    ? "border border-slate-300 text-slate-400 bg-slate-100 dark:bg-slate-800 dark:border-slate-700 cursor-not-allowed"
                    : "border border-emerald-500 text-emerald-600 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
                }`}
              >
                💵 Cobrar seleccionados
              </button>

              <button
                type="button"
                disabled={pedido.facturado || !todosSeleccionados}
                onClick={() => {
                  setModo("TODO");
                  confirmarFactura();
                }}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  pedido.facturado || !todosSeleccionados
                    ? "border border-slate-300 text-slate-400 bg-slate-100 dark:bg-slate-800 dark:border-slate-700 cursor-not-allowed"
                    : "border border-sky-600 bg-sky-600 text-white hover:bg-sky-700"
                }`}
              >
                🧾 Cobrar todo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal pago en efectivo */}
      {mostrarPagoEfectivo && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
          <div className="bg-white dark:!bg-slate-900 text-slate-900 dark:text-slate-100 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 max-w-md w-[95%] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:!bg-slate-800">
              <h3 className="text-sm md:text-base font-semibold flex items-center gap-2">
                💵 Pago en efectivo
              </h3>
              <button
                type="button"
                onClick={() => setMostrarPagoEfectivo(false)}
                className="text-xs px-2 py-1 rounded-lg border border-slate-400 dark:border-slate-600 
                          bg-slate-100 dark:bg-slate-800 
                          text-slate-700 dark:text-slate-200 
                          hover:bg-slate-200 dark:hover:bg-slate-700 transition"
              >
                Cerrar
              </button>
            </div>

            {/* Body */}
            <div className="px-4 py-4 text-xs sm:text-sm space-y-3">
              <p className="text-slate-800 dark:text-slate-100">
                <span className="font-semibold">Total a pagar:</span>{" "}
                ${facturaPendiente?.total?.toFixed(2)}
              </p>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-200">
                  ¿Con cuánto paga el cliente?
                </label>
                <input
                  type="number"
                  min="0"
                  value={montoPago}
                  onChange={(e) => setMontoPago(e.target.value)}
                  placeholder="Ingrese monto recibido"
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                />
              </div>

              {montoPago &&
                facturaPendiente?.total &&
                parseFloat(montoPago) >= facturaPendiente.total && (
                  <p className="mt-1 text-xs sm:text-sm font-semibold text-emerald-500 dark:text-emerald-300">
                    Vueltos: $
                    {(parseFloat(montoPago) - facturaPendiente.total).toFixed(
                      2
                    )}
                  </p>
                )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 bg-slate-50 dark:!bg-slate-800">
              <button
                type="button"
                onClick={() => setMostrarPagoEfectivo(false)}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg 
                          border border-slate-400 dark:border-slate-600
                          bg-slate-100 dark:bg-slate-800
                          text-slate-700 dark:text-slate-200 
                          hover:bg-slate-200 dark:hover:bg-slate-700 transition"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarPagoEfectivo}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold border border-emerald-500 transition-colors"
              >
                Confirmar pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmación factura */}
      {mostrarConfirmacion && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
          <div className="bg-white dark:!bg-slate-900 text-slate-900 dark:text-slate-100 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 max-w-md w-[95%] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:!bg-slate-800">
              <h3 className="text-sm md:text-base font-semibold">
                ✅ Factura generada
              </h3>
              <button
                type="button"
                onClick={() => setMostrarConfirmacion(false)}
                className="text-xs px-2 py-1 rounded-lg border border-slate-400 dark:border-slate-600
                          bg-slate-100 dark:bg-slate-800
                          text-slate-700 dark:text-slate-200
                          hover:bg-slate-200 dark:hover:bg-slate-700 transition"
              >
                Cerrar
              </button>
            </div>

            {/* Body */}
            <div className="px-4 py-4 text-xs sm:text-sm space-y-2 text-slate-800 dark:text-slate-100">
              <p>La factura se ha generado correctamente.</p>
              <p>¿Qué deseas hacer ahora?</p>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 bg-slate-50 dark:!bg-slate-800">
              <button
                type="button"
                onClick={() => setMostrarConfirmacion(false)}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg 
                          border border-slate-400 dark:border-slate-600
                          bg-slate-100 dark:bg-slate-800
                          text-slate-700 dark:text-slate-200 
                          hover:bg-slate-200 dark:hover:bg-slate-700 transition"
              >
                Permanecer aquí
              </button>

              {/* 🖨️ Botón imprimir factura */}
              <button
                type="button"
                disabled={!facturaGeneradaId}
                onClick={imprimirFactura}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold border border-purple-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                🖨️ Imprimir
              </button>

              <button
                type="button"
                onClick={() => navigate("/pedidos")}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold border border-sky-500 transition-colors"
              >
                ⬅️ Volver al listado
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FacturarPedido;
